import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { rupiah } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList } from "recharts";
import { toast } from "sonner";

const BLN = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const short = (n) => (n >= 1e6 ? `${+(n / 1e6).toFixed(1)}jt` : n >= 1e3 ? `${Math.round(n / 1e3)}rb` : n ? String(n) : "");
const thisYear = new Date().getFullYear();

const Stat = ({ label, value, testid, cls }) => (
  <div className={`rounded-xl p-4 text-white shadow-sm ${cls}`} data-testid={testid}>
    <div className="text-xs font-semibold opacity-90">{label}</div>
    <div className="mt-1 font-mono text-xl sm:text-2xl font-black">{value}</div>
  </div>
);

export default function NotaRekap() {
  const [tahun, setTahun] = useState(String(thisYear));
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(`/nota-pengajuan-rekap?tahun=${tahun}`).then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [tahun]);
  const chart = (d?.bulan || []).map((b) => ({ ...b, nama: BLN[b.bulan - 1] }));
  const aktif = chart.filter((b) => b.total > 0).length;
  return (
    <div className="space-y-5">
      <PageHeader title="Rekap Belanja Bulanan" subtitle="Total nota yang sudah disetujui, per bulan dan per pengaju (berdasarkan tanggal belanja)">
        <Select value={tahun} onValueChange={setTahun}>
          <SelectTrigger className="h-10 w-32" data-testid="rekap-tahun"><SelectValue /></SelectTrigger>
          <SelectContent>{[0, 1, 2, 3].map((i) => <SelectItem key={i} value={String(thisYear - i)}>{thisYear - i}</SelectItem>)}</SelectContent>
        </Select>
      </PageHeader>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat label={`Total Disetujui ${tahun}`} value={rupiah(d?.total || 0)} testid="rekap-total" cls="bg-blue-900" />
        <Stat label="Jumlah Nota" value={d?.jumlah || 0} testid="rekap-jumlah" cls="bg-emerald-600" />
        <Stat label="Rata-rata / Bulan Aktif" value={rupiah(aktif ? Math.round((d?.total || 0) / aktif) : 0)} testid="rekap-rata" cls="bg-orange-600" />
      </div>
      <Card className="p-3 sm:p-5" data-testid="rekap-chart">
        <div className="mb-3 text-sm font-bold text-slate-700">Total per Bulan</div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart} margin={{ top: 20, right: 4, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="nama" tick={{ fontSize: 11 }} interval={0} />
              <YAxis tickFormatter={short} tick={{ fontSize: 10 }} width={42} />
              <Tooltip formatter={(v) => rupiah(v)} labelFormatter={(l) => `${l} ${tahun}`} />
              <Bar dataKey="total" name="Total disetujui" fill="#ea580c" radius={[4, 4, 0, 0]}><LabelList dataKey="total" position="top" formatter={short} style={{ fontSize: 10, fontWeight: 700 }} /></Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
      <PengajuTable d={d} />
    </div>
  );
}

const PengajuTable = ({ d }) => (
  <Card className="p-0 overflow-hidden">
    <div className="px-4 py-3 text-sm font-bold text-slate-700">Rincian per Pengaju</div>
    <div className="overflow-x-auto">
      <table className="w-full text-xs sm:text-sm" data-testid="rekap-table">
        <thead><tr className="bg-blue-900 text-white uppercase text-[11px]">
          <th className="sticky left-0 bg-blue-900 px-3 py-2.5 text-left">Pengaju</th>
          {BLN.map((b) => <th key={b} className="px-2 py-2.5 text-right">{b}</th>)}
          <th className="px-3 py-2.5 text-right">Nota</th><th className="px-3 py-2.5 text-right">Total</th>
        </tr></thead>
        <tbody>
          {!d?.pengaju.length && <tr><td colSpan={15} className="py-10 text-center text-slate-400">Belum ada nota disetujui di tahun ini.</td></tr>}
          {d?.pengaju.map((p, i) => (
            <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`rekap-row-${i}`}>
              <td className="sticky left-0 bg-white px-3 py-2 font-semibold whitespace-nowrap">{p.nama}</td>
              {p.per_bulan.map((v, m) => <td key={m} className="px-2 py-2 text-right font-mono whitespace-nowrap">{v ? short(v) : "-"}</td>)}
              <td className="px-3 py-2 text-right font-mono">{p.jumlah}</td>
              <td className="px-3 py-2 text-right font-mono font-bold whitespace-nowrap">{rupiah(p.total)}</td>
            </tr>
          ))}
          {!!d?.pengaju.length && (
            <tr className="bg-orange-50 font-bold" data-testid="rekap-row-total">
              <td className="sticky left-0 bg-orange-50 px-3 py-2">TOTAL</td>
              {d.bulan.map((b) => <td key={b.bulan} className="px-2 py-2 text-right font-mono whitespace-nowrap">{b.total ? short(b.total) : "-"}</td>)}
              <td className="px-3 py-2 text-right font-mono">{d.jumlah}</td>
              <td className="px-3 py-2 text-right font-mono whitespace-nowrap">{rupiah(d.total)}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </Card>
);
